import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { FiCamera, FiCheck, FiClock, FiRepeat, FiXCircle } from 'react-icons/fi';
import Tooltip from '../../../../../components/Tooltip';
import firebaseFirestore from '~/config/firebase/firestore';
import TarjetaSerie, { FechaDeSerie } from './TarjetaSerie';

/**
 * Las fechas de una reserva que se repite, vistas desde el panel.
 *
 * Es solo para mirar: cada fecha se cambia o se cancela desde su propia fila de
 * la tabla, como cualquier otra reserva. Aquí se ve la serie entera de un
 * vistazo, que es lo que no se podía saber antes.
 */

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** La reserva desde la que se abrió, para saber qué serie mostrar. */
  reserva: any;
};

type Fila = FechaDeSerie & {
  indice?: number;
};

const DetalleSerie = ({ isOpen, onClose, reserva }: Props) => {
  const [filas, setFilas] = useState<Fila[]>([]);
  const [cargando, setCargando] = useState(true);
  // La tarjeta de la serie, para la captura de pantalla.
  const [tarjetaAbierta, setTarjetaAbierta] = useState(false);

  const recurrencia = reserva?.recurrence;

  useEffect(() => {
    if (!isOpen || !recurrencia?.id) return;
    let vivo = true;
    (async () => {
      setCargando(true);
      try {
        const snap = await getDocs(
          query(
            collection(firebaseFirestore, 'reservations'),
            where('recurrence.id', '==', recurrencia.id)
          )
        );
        if (!vivo) return;
        const lista: Fila[] = snap.docs.map((d) => {
          const r = d.data() as any;
          return {
            id: d.id,
            code: r.code ?? '',
            cuando: r.startDatetime?.toDate?.() ?? r.datetime?.toDate?.() ?? null,
            sala: r.location?.name ?? '',
            horaInicio: r.startHour ?? '',
            horaFin: r.endHour && r.endHour !== '--' ? r.endHour : '',
            estado: r.status ?? 'pending',
            indice: r.recurrence?.indice,
            personas: Number(r.numberPeople ?? 0) || undefined,
          };
        });
        lista.sort((a, b) => (a.cuando?.getTime() ?? 0) - (b.cuando?.getTime() ?? 0));
        setFilas(lista);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [isOpen, recurrencia?.id]);

  const ahora = Date.now();
  const activas = filas.filter(
    (f) => f.estado !== 'cancelled' && (f.cuando?.getTime() ?? 0) > ahora
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg' isCentered scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent>
        <ModalHeader pb={2}>
          <HStack spacing={2}>
            <Icon as={FiRepeat} color='blue.500' />
            <Text fontSize='md'>Esta reserva se repite</Text>
          </HStack>
          <Text fontSize='sm' fontWeight='normal' color='gray.500' mt={1}>
            {recurrencia?.descripcion ?? ''}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={4}>
          {cargando ? (
            <VStack align='stretch' spacing={2}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} h='46px' borderRadius='md' />
              ))}
            </VStack>
          ) : (
            <>
              <HStack spacing={4} mb={3} fontSize='sm'>
                <Text>
                  <b>{filas.length}</b> {filas.length === 1 ? 'reserva' : 'reservas'} en la serie
                </Text>
                <Text color='gray.500'>
                  {activas.length} {activas.length === 1 ? 'por venir' : 'por venir'}
                </Text>
              </HStack>

              <VStack align='stretch' spacing={1.5}>
                {filas.map((fila) => {
                  const cancelada = fila.estado === 'cancelled';
                  const pasada = (fila.cuando?.getTime() ?? 0) < ahora;
                  const esta = fila.id === reserva?.id;
                  return (
                    <Flex
                      key={fila.id}
                      align='center'
                      gap={3}
                      px={3}
                      py={2}
                      borderWidth={esta ? '2px' : '1px'}
                      borderColor={esta ? 'blue.400' : 'gray.200'}
                      bg={esta ? 'blue.50' : cancelada || pasada ? 'gray.50' : 'white'}
                      borderRadius='md'
                      opacity={cancelada ? 0.6 : 1}
                    >
                      <Icon
                        as={cancelada ? FiXCircle : pasada ? FiClock : FiCheck}
                        color={cancelada ? 'red.400' : pasada ? 'gray.400' : 'green.500'}
                        boxSize={4}
                        flexShrink={0}
                      />
                      <Box flex={1} minW={0}>
                        <Text
                          fontSize='sm'
                          noOfLines={1}
                          textDecoration={cancelada ? 'line-through' : undefined}
                          sx={{ '&::first-letter': { textTransform: 'uppercase' } }}
                        >
                          {fila.cuando
                            ? format(fila.cuando, "EEEE d 'de' MMMM", { locale: esLocale })
                            : '—'}
                        </Text>
                        <Text fontSize='xs' color='gray.600' noOfLines={1}>
                          {fila.horaInicio}
                          {fila.horaFin ? `–${fila.horaFin}` : ''} · {fila.sala}
                        </Text>
                      </Box>
                      <Badge
                        colorScheme={cancelada ? 'red' : esta ? 'blue' : 'gray'}
                        fontSize='0.6em'
                        flexShrink={0}
                      >
                        {cancelada ? 'Cancelada' : fila.code}
                      </Badge>
                    </Flex>
                  );
                })}
              </VStack>

              <Text fontSize='xs' color='gray.500' mt={4}>
                Cada fecha se cambia o se cancela por separado, desde su fila en la tabla.
              </Text>
            </>
          )}
        </ModalBody>

        <ModalFooter justifyContent='space-between'>
          {/* La tarjeta de toda la serie, como la de una reserva suelta, para
              hacerle captura y mandársela al cliente. */}
          <Tooltip label='Tarjeta de la serie (para captura de pantalla)' openDelay={400}>
            <IconButton
              aria-label='Ver tarjeta de la serie'
              icon={<FiCamera />}
              size='sm'
              variant='ghost'
              color='gray.500'
              _hover={{ color: 'blue.500', bg: 'blue.50' }}
              isDisabled={cargando || filas.length === 0}
              onClick={() => setTarjetaAbierta(true)}
            />
          </Tooltip>
          <Button size='sm' onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>

        {/* Dentro del contenido, no al lado: si cuelga fuera, el bloqueo de
            scroll de esta modal cancela la rueda del ratón sobre la tarjeta. */}
        <TarjetaSerie
          isOpen={tarjetaAbierta}
          onClose={() => setTarjetaAbierta(false)}
          reserva={reserva}
          fechas={filas}
        />
      </ModalContent>
    </Modal>
  );
};

export default DetalleSerie;
